<script setup lang="ts">
import type { JsonValue } from "./jsonValue";
import { computed, onBeforeUnmount, ref, useId, watch } from "vue";
import { useI18n } from "vue-i18n";
import VIcon from "../Icon/VIcon.vue";
import { JSON_TOKEN_CLASS } from "./jsonColors";
import { formatJson, parseJson, toJsonValue, tokenizeJson } from "./jsonValue";
import VJsonTreeNode from "./VJsonTreeNode.vue";

defineOptions({
  name: "VJsonEditor",
});

/**
 * Shows a value as coloured JSON, for debug screens. `text` mode shows the JSON text; without `readOnly` that text can
 * be edited, and only valid JSON is passed back (an error below the text says where it breaks). `tree` mode shows
 * objects and arrays that open and close, and is always read only.
 */
const {
  modelValue = null,
  mode = "text",
  readOnly = false,
  debounce = 0,
  toolbar = true,
  initialDepth = 2,
} = defineProps<{
  modelValue?: unknown
  mode?: "text" | "tree"
  readOnly?: boolean
  /** Milliseconds to wait after the last valid edit before passing it back. */
  debounce?: number
  /** Shows the copy button (and expand and collapse all in tree mode, format while editing). */
  toolbar?: boolean
  /** Tree mode: levels that start open. */
  initialDepth?: number
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: JsonValue): void
}>();

const { t } = useI18n();
const errorId = useId();

const editable = computed(() => mode === "text" && !readOnly);
const formatted = computed(() => formatJson(modelValue));
const treeValue = computed(() => toJsonValue(modelValue));

const error = ref<{ message: string, line: number | null, column: number | null } | null>(null);

// The text being edited. It follows the value from outside, except when that value is the one this editor just
// passed back: rewriting the text then would move the caret and drop the user's own formatting.
const text = ref(formatted.value);
let lastEmitted: string | null = null;
watch(formatted, (next) => {
  if (next !== lastEmitted) {
    text.value = next;
    error.value = null;
  }
});

let timer: ReturnType<typeof setTimeout> | null = null;

function onInput(event: Event) {
  text.value = (event.target as HTMLTextAreaElement).value;
  const result = parseJson(text.value);
  if (!result.ok) {
    error.value = { message: result.message, line: result.line, column: result.column };
    return;
  }
  error.value = null;
  if (timer) {
    clearTimeout(timer);
  }
  const value = result.value;
  function send() {
    lastEmitted = formatJson(value);
    emit("update:modelValue", value);
  }
  if (debounce > 0) {
    timer = setTimeout(send, debounce);
  } else {
    send();
  }
}

onBeforeUnmount(() => {
  if (timer) {
    clearTimeout(timer);
  }
});

const shownText = computed(() => (editable.value ? text.value : formatted.value));
const tokens = computed(() => tokenizeJson(shownText.value));

const errorText = computed(() => {
  if (!error.value) {
    return "";
  }
  return error.value.line === null
    ? t("base.json_editor.invalid")
    : t("base.json_editor.invalid_at", { line: error.value.line, column: error.value.column });
});

function formatText() {
  const result = parseJson(text.value);
  if (result.ok) {
    text.value = formatJson(result.value);
  }
}

const expandAll = ref<{ open: boolean, sequence: number } | null>(null);
function setAllOpen(open: boolean) {
  expandAll.value = { open, sequence: (expandAll.value?.sequence ?? 0) + 1 };
}

const copied = ref(false);
let copiedTimer: ReturnType<typeof setTimeout> | null = null;
async function copy() {
  try {
    await navigator.clipboard.writeText(editable.value ? text.value : formatted.value);
    copied.value = true;
    if (copiedTimer) {
      clearTimeout(copiedTimer);
    }
    copiedTimer = setTimeout(() => (copied.value = false), 1500);
  } catch {
    copied.value = false;
  }
}

onBeforeUnmount(() => {
  if (copiedTimer) {
    clearTimeout(copiedTimer);
  }
});

const toolbarButtonClass = "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-2 focus-visible:outline-primary-500 disabled:opacity-50 dark:text-gray-300 dark:hover:bg-dark-700 dark:hover:text-white";
</script>

<template>
  <div
    class="v-json-editor flex min-h-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white font-mono text-xs dark:border-dark-600 dark:bg-dark-900"
    :data-mode="mode"
    data-testid="json-editor"
  >
    <div
      v-if="toolbar"
      class="flex shrink-0 items-center justify-end gap-1 border-b border-gray-200 bg-gray-50 px-2 py-1 font-sans dark:border-dark-600 dark:bg-dark-800"
    >
      <template v-if="mode === 'tree'">
        <button type="button" :class="toolbarButtonClass" data-testid="json-expand-all" @click="setAllOpen(true)">
          <VIcon name="chevrons-up-down" />{{ t("base.json_editor.expand_all") }}
        </button>
        <button type="button" :class="toolbarButtonClass" data-testid="json-collapse-all" @click="setAllOpen(false)">
          <VIcon name="chevrons-down-up" />{{ t("base.json_editor.collapse_all") }}
        </button>
      </template>
      <button
        v-if="editable"
        type="button"
        :class="toolbarButtonClass"
        :disabled="error !== null"
        data-testid="json-format"
        @click="formatText"
      >
        <VIcon name="text-align-start" />{{ t("base.json_editor.format") }}
      </button>
      <button type="button" :class="toolbarButtonClass" data-testid="json-copy" @click="copy">
        <VIcon :name="copied ? 'check' : 'copy'" />{{ copied ? t("base.copied") : t("base.copy") }}
      </button>
    </div>

    <div class="min-h-0 flex-1 overflow-auto p-3">
      <div v-if="mode === 'tree'" class="pl-4" data-testid="json-tree">
        <VJsonTreeNode :value="treeValue" :initial-depth="initialDepth" :expand-all="expandAll" />
      </div>
      <div v-else class="grid min-w-max">
        <!-- Not a <pre>: the formatter may put the token spans on their own lines, and only outside <pre> does Vue drop
             that whitespace. v-text keeps each token's text exact; the trailing newline gives the last line its height. -->
        <code
          class="col-start-1 row-start-1 m-0 block whitespace-pre leading-5"
          :aria-hidden="editable ? 'true' : undefined"
          data-testid="json-text"
        >
          <span
            v-for="(token, index) in tokens"
            :key="index"
            :class="JSON_TOKEN_CLASS[token.type]"
            v-text="token.text"
          />
          <span v-text="'\n'" />
        </code>
        <textarea
          v-if="editable"
          :value="text"
          class="col-start-1 row-start-1 m-0 resize-none overflow-hidden whitespace-pre border-0 bg-transparent p-0 font-mono text-xs leading-5 text-transparent caret-gray-900 outline-none dark:caret-gray-100"
          spellcheck="false"
          autocomplete="off"
          autocapitalize="off"
          wrap="off"
          :aria-label="t('base.json_editor.label')"
          :aria-invalid="error !== null"
          :aria-describedby="error ? errorId : undefined"
          data-testid="json-textarea"
          @input="onInput"
        />
      </div>
    </div>

    <p
      v-if="error"
      :id="errorId"
      role="alert"
      class="m-0 shrink-0 border-t border-red-200 bg-red-50 px-3 py-1.5 font-sans text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300"
      data-testid="json-error"
    >
      {{ errorText }}
    </p>
  </div>
</template>
